import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import stockApi from '../services/stockApi';

// «المخزون» (tasks/stock.md step 4). Every figure on the page is a SERVER answer: each action
// invalidates the stock queries rather than patching a number locally — a client-side guess at a new
// total is how the page starts disagreeing with the history.

export const STOCK_KEY = ['stock'];

const EMPTY_SUMMARY = { store: null, recountOverdueDays: 7, items: [], counts: { negative: 0, low: 0, recountOverdue: 0 } };

/** Unit codes, period presets, overdue threshold. Fixed per release — read once. */
export const useStockOptions = () => useQuery({
  queryKey: [...STOCK_KEY, 'options'],
  queryFn: () => stockApi.getOptions(),
  staleTime: Infinity
});

export const useStockStores = () => useQuery({
  queryKey: [...STOCK_KEY, 'stores'],
  queryFn: async () => (await stockApi.getStores()).stores,
  staleTime: 5 * 60 * 1000
});

/** The item list. A minute's poll: another ops person may record a handover meanwhile. */
export const useStockSummary = (storeId, includeArchived) => {
  const result = useQuery({
    queryKey: [...STOCK_KEY, 'summary', storeId || null, Boolean(includeArchived)],
    queryFn: () => stockApi.getSummary({ storeId, includeArchived }),
    enabled: Boolean(storeId),
    refetchInterval: 60 * 1000,
    staleTime: 15 * 1000,
    placeholderData: (previous) => previous
  });
  return { ...result, summary: result.data ?? EMPTY_SUMMARY };
};

/** The per-person check — read only while its tab is open. */
export const useStockPeople = (storeId, period, enabled) => useQuery({
  queryKey: [...STOCK_KEY, 'people', storeId || null, period],
  queryFn: () => stockApi.getPeople({ storeId, period }),
  enabled: Boolean(storeId) && Boolean(enabled),
  staleTime: 30 * 1000,
  placeholderData: (previous) => previous
});

/** One item's history — read only while its window is open. */
export const useStockHistory = (storeId, itemId) => useQuery({
  queryKey: [...STOCK_KEY, 'history', storeId || null, itemId || null],
  queryFn: () => stockApi.getHistory({ storeId, itemId }),
  enabled: Boolean(storeId) && Boolean(itemId),
  staleTime: 10 * 1000
});

/** The handover picker — read only while the handover form is open. */
export const useStockTakers = (enabled) => useQuery({
  queryKey: [...STOCK_KEY, 'takers'],
  queryFn: async () => (await stockApi.getTakers()).takers,
  enabled: Boolean(enabled),
  staleTime: 60 * 1000
});

// onSettled, not onSuccess (the Reviews page rule): a refusal usually means the page was stale, which is
// exactly when a refresh is most needed. The promise is RETURNED so the action stays pending until the
// fresh figures are in.
//
// Retry: once, and ONLY when no answer came back (network drop, timeout). A refusal from the server
// (duplicate name, archived item…) is final — retrying it only delays the message by a second, and in
// a background tab React Query parks the retry until the tab returns, leaving the spinner running
// (UI test 2026-10-12). A retry without an answer is safe: stock entries and «إضافة صنف» carry an
// idempotency key; the other writes set a value, so doing one twice changes nothing.
const retryOnlyWithoutAnswer = (failureCount, error) => !error?.response && failureCount < 1;

const useAction = (mutationFn) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    retry: retryOnlyWithoutAnswer,
    onSettled: () => queryClient.invalidateQueries({ queryKey: STOCK_KEY })
  });
};

export const useCreateStore = () => useAction(stockApi.createStore);
export const useCreateStockItem = () => useAction(stockApi.createItem);
export const useUpdateStockItem = () => useAction(stockApi.updateItem);
export const useArchiveStockItem = () => useAction(stockApi.archiveItem);
export const useRestoreStockItem = () => useAction(stockApi.restoreItem);
export const useUploadStockPhoto = () => useAction(stockApi.uploadPhoto);
export const useRecordPurchase = () => useAction(stockApi.recordPurchase);
export const useRecordTakeOut = () => useAction(stockApi.recordTakeOut);
export const useRecordRecount = () => useAction(stockApi.recordRecount);
export const useReverseStockEntry = () => useAction(stockApi.reverseEntry);
